import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Users, Globe, Activity, Clock, Download, RefreshCw, Eye, Monitor, Smartphone, Tablet, Heart } from 'lucide-react';
import LiveVisitorsMap from '../../components/traffic/LiveVisitorsMap';
import ActivityFeed from '../../components/traffic/ActivityFeed';
import AnalyticsCharts from '../../components/traffic/AnalyticsCharts';
import VisitorDetails from '../../components/traffic/VisitorDetails';
import TrafficSources from '../../components/traffic/TrafficSources';
import NotificationPanel from '../../components/traffic/NotificationPanel';
import NotificationBox from '../../components/traffic/NotificationBox';
import io from 'socket.io-client';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Heartbeat Animation Component
const HeartbeatIndicator = ({ isActive }) => {
  return (
    <div className="relative flex items-center justify-center">
      <style jsx>{`
        @keyframes heartbeat {
          0%, 100% { transform: scale(1); opacity: 1; }
          15% { transform: scale(1.3); opacity: 0.8; }
          30% { transform: scale(1); opacity: 1; }
          45% { transform: scale(1.25); opacity: 0.8; }
          60% { transform: scale(1); opacity: 1; }
        }
        
        @keyframes pulse-ring {
          0% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.5); opacity: 0.3; }
          100% { transform: scale(2); opacity: 0; }
        }

        .heartbeat {
          animation: heartbeat 2s ease-in-out infinite;
        }

        .pulse-ring {
          animation: pulse-ring 2s ease-out infinite;
        }
      `}</style>
      
      {isActive && (
        <>
          {/* Pulsing rings */}
          <div className="absolute w-12 h-12 bg-red-400 rounded-full pulse-ring opacity-0"></div>
          <div className="absolute w-12 h-12 bg-red-400 rounded-full pulse-ring opacity-0" style={{ animationDelay: '1s' }}></div>
          
          {/* Heartbeat icon */}
          <Heart 
            className="heartbeat relative z-10" 
            size={24}
            fill="currentColor"
            color="#ef4444"
          />
        </>
      )}
      
      {!isActive && (
        <Heart size={24} color="#9ca3af" />
      )}
    </div>
  );
};

const LiveTrafficDashboard = () => {
  const [liveVisitors, setLiveVisitors] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [timeRange, setTimeRange] = useState('today');
  const [loading, setLoading] = useState(true);
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const pollingIntervalRef = React.useRef(null);
  const lastFetchTimeRef = React.useRef(Date.now());

  // Function to start polling
  const startPolling = () => {
    console.log('Starting live polling...');
    setIsConnected(true);
    
    // Clear any existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    // Poll for both live visitors AND analytics every 3 seconds
    pollingIntervalRef.current = setInterval(() => {
      fetchLiveVisitors();
      fetchAnalytics();
      lastFetchTimeRef.current = Date.now();
    }, 3000);

    // Initial fetch
    fetchLiveVisitors();
    fetchAnalytics();
    lastFetchTimeRef.current = Date.now();
  };

  // Function to stop polling
  const stopPolling = () => {
    console.log('Stopping polling...');
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsConnected(false);
  };

  // Initialize live data polling
  useEffect(() => {
    startPolling();

    return () => {
      stopPolling();
    };
  }, []);

  // Handle Page Visibility API to prevent sleep mode issues
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab became hidden - but DON'T stop polling
        console.log('Tab hidden - keeping polling active');
        // Force a fetch to prove we're still alive
        setTimeout(() => {
          fetchLiveVisitors();
          fetchAnalytics();
        }, 1000);
      } else {
        // Tab became visible - ensure polling is active
        console.log('Tab visible - ensuring polling is active');
        
        // Check if last fetch was more than 10 seconds ago
        const timeSinceLastFetch = Date.now() - lastFetchTimeRef.current;
        if (timeSinceLastFetch > 10000) {
          console.log('Restarting polling after wake...');
          stopPolling();
          startPolling();
        }
      }
    };

    // Add visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Add focus listener as backup
    window.addEventListener('focus', () => {
      console.log('Window focused - checking polling...');
      if (!pollingIntervalRef.current) {
        startPolling();
      }
    });

    // Add blur listener to log when focus is lost
    window.addEventListener('blur', () => {
      console.log('Window lost focus - maintaining polling');
    });

    // Prevent page unload accidentally
    const handleBeforeUnload = (e) => {
      // Only show warning if there are live visitors
      if (liveVisitors.length > 0) {
        e.preventDefault();
        e.returnValue = 'Live monitoring is active. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [liveVisitors.length]);

  // Keep-alive mechanism - check if polling is still working every 5 seconds
  useEffect(() => {
    const keepAliveInterval = setInterval(() => {
      const timeSinceLastFetch = Date.now() - lastFetchTimeRef.current;
      
      // If no fetch in last 7 seconds (more than 2 polling cycles), restart polling
      if (timeSinceLastFetch > 7000) {
        console.log('No fetch detected in 7s - restarting polling');
        stopPolling();
        startPolling();
      }
      
      // Ensure polling interval exists
      if (!pollingIntervalRef.current) {
        console.log('Polling interval lost - restarting');
        startPolling();
      }
    }, 5000);

    return () => clearInterval(keepAliveInterval);
  }, []);

  // Additional heartbeat to keep browser tab active
  useEffect(() => {
    const heartbeatInterval = setInterval(() => {
      // Perform a lightweight operation to keep the browser active
      document.title = `NETIX LIVE - ${liveVisitors.length} Visitors`;
    }, 1000);

    return () => clearInterval(heartbeatInterval);
  }, [liveVisitors.length]);

  // Request Wake Lock to prevent screen/device sleep (if supported)
  useEffect(() => {
    let wakeLock = null;

    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLock = await navigator.wakeLock.request('screen');
          console.log('Wake Lock acquired - screen will stay active');
          
          wakeLock.addEventListener('release', () => {
            console.log('Wake Lock released - attempting to reacquire');
            // Attempt to reacquire immediately
            setTimeout(requestWakeLock, 100);
          });
        } else {
          console.log('Wake Lock API not supported - using alternative keep-alive');
        }
      } catch (err) {
        console.log('Wake Lock error:', err);
        // Retry after a short delay
        setTimeout(requestWakeLock, 5000);
      }
    };

    requestWakeLock();

    // Re-acquire wake lock when page becomes visible again
    const handleVisibilityForWakeLock = async () => {
      if (!document.hidden && 'wakeLock' in navigator) {
        await requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityForWakeLock);

    // Periodically check and reacquire wake lock every 30 seconds
    const wakeLockCheckInterval = setInterval(() => {
      if (wakeLock === null || wakeLock.released) {
        console.log('Wake lock check - reacquiring');
        requestWakeLock();
      }
    }, 30000);

    return () => {
      if (wakeLock) {
        wakeLock.release();
      }
      clearInterval(wakeLockCheckInterval);
      document.removeEventListener('visibilitychange', handleVisibilityForWakeLock);
    };
  }, []);

  // Fetch data when timeRange changes
  useEffect(() => {
    if (timeRange) {
      fetchAnalytics();
    }
  }, [timeRange]);

  const fetchLiveVisitors = async () => {
    try {
      setIsRefreshing(true);
      const token = localStorage.getItem('customerAdminToken');
      if (!token) return; // Don't fetch if no token yet
      
      const response = await fetch(`${API_BASE_URL}/api/traffic/live-visitors`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          // Token expired - redirect to login
          localStorage.removeItem('customerAdminToken');
          window.location.href = '/customer-admin/login';
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      if (data.success && data.visitors) {
        setLiveVisitors(data.visitors || []);
        setLastRefreshed(new Date());
      }
    } catch (error) {
      console.error('Error fetching live visitors:', error);
      // Silently fail - don't show error toast on page load
    } finally {
      setTimeout(() => setIsRefreshing(false), 500); // Short delay for visual feedback
    }
  };

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('customerAdminToken');
      if (!token) {
        setLoading(false);
        return; // Don't fetch if no token yet
      }
      
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/traffic/analytics?time_range=${timeRange}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        console.error('Analytics API error:', response.status, response.statusText);
        if (response.status === 401) {
          // Token expired - redirect to login
          localStorage.removeItem('customerAdminToken');
          window.location.href = '/customer-admin/login';
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      if (data.success && data.analytics) {
        setAnalytics(data.analytics);
        setLastRefreshed(new Date());
      } else {
        console.error('Invalid analytics data:', data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      // Only show toast error if it's a real error (not just missing token on initial load)
      const token = localStorage.getItem('customerAdminToken');
      if (token) {
        toast.error('Failed to load analytics. Please refresh.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      const token = localStorage.getItem('customerAdminToken');
      const response = await fetch(`${API_BASE_URL}/api/traffic/export/excel?time_range=${timeRange}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `traffic_analytics_${timeRange}_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Excel exported successfully');
      } else {
        toast.error('Failed to export data');
      }
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Failed to export data');
    }
  };

  // Manual refresh handler
  const handleManualRefresh = () => {
    fetchLiveVisitors();
    fetchAnalytics();
    toast.success('Data refreshed!');
  };

  // Alias for compatibility
  const exportToExcel = handleExportExcel;

  const getDeviceIcon = (deviceType) => {
    switch(deviceType?.toLowerCase()) {
      case 'mobile': return <Smartphone className="h-4 w-4" />;
      case 'tablet': return <Tablet className="h-4 w-4" />;
      default: return <Monitor className="h-4 w-4" />;
    }
  };

  const deviceBreakdown = analytics?.deviceBreakdown || [];
  const mobileCount = deviceBreakdown.find(d => d.device === 'Mobile')?.count || 0;
  const desktopCount = deviceBreakdown.find(d => d.device === 'Desktop')?.count || 0;
  const tabletCount = deviceBreakdown.find(d => d.device === 'Tablet')?.count || 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-2">
      {/* Header */}
      <div className="mb-2">
        <div className="flex items-center justify-between mb-1.5">
          <div>
            <div className="flex items-center gap-1.5">
              <div className="bg-gradient-to-br from-red-50 to-pink-50 p-1.5 rounded-md border border-red-200 shadow-sm">
                <div className="scale-50">
                  <HeartbeatIndicator isActive={isConnected} />
                </div>
              </div>
              <div>
                <h1 className="text-base font-bold text-gray-900 flex items-center gap-1.5">
                  <Activity className="h-4 w-4 text-emerald-600" />
                  NETIX LIVE TRAFFIC MONITOR
                </h1>
                <p className="text-gray-600 text-xs flex items-center gap-1.5">
                  <span className="text-xs">Real-time analytics</span>
                  {isConnected && (
                    <span className="text-xs bg-red-100 text-red-700 px-1 py-0.5 rounded-full font-semibold animate-pulse">
                      ● LIVE
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {/* Notification Panel */}
            <NotificationPanel />
            
            <Button 
              onClick={() => window.location.href = '/customer-admin/dashboard'}
              className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs px-2"
              size="sm"
            >
              Home
            </Button>
            {/* LIVE ON AIR Card */}
            <div className="bg-gradient-to-br from-green-400 to-emerald-500 text-white px-3 py-2 rounded-lg border-2 border-green-600 shadow-lg w-[110px] h-[60px]">
              <div className="flex flex-col justify-center h-full">
                <div className="flex items-center gap-1.5">
                  <div className="relative">
                    <div className="w-2 h-2 rounded-full bg-white animate-ping absolute"></div>
                    <div className="w-2 h-2 rounded-full bg-white relative"></div>
                  </div>
                  <span className="font-bold text-sm">LIVE</span>
                </div>
                <div className="text-xs opacity-90 mt-0.5">Auto: 3s</div>
              </div>
            </div>
            
            {/* Last Updated Card */}
            <div className="bg-gradient-to-br from-blue-400 to-cyan-500 text-white px-3 py-2 rounded-lg border-2 border-blue-600 shadow-lg w-[130px] h-[60px]">
              <div className="flex flex-col justify-center h-full">
                <div className="flex items-center gap-1.5">
                  <RefreshCw className={`h-3 w-3 ${isRefreshing ? 'animate-spin' : ''}`} />
                  <span className="text-xs font-semibold">Updated</span>
                </div>
                <div className="text-sm font-bold mt-0.5">{lastRefreshed.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour12: true })}</div>
              </div>
            </div>
            
            {/* Reconnect Card/Button */}
            <div className="bg-gradient-to-br from-purple-400 to-pink-500 text-white px-3 py-2 rounded-lg border-2 border-purple-600 shadow-lg w-[110px] h-[60px] cursor-pointer hover:scale-105 transition-transform"
              onClick={() => {
                console.log('Manual reconnect triggered');
                stopPolling();
                startPolling();
                toast.success('Connection refreshed!');
              }}
            >
              <div className="flex flex-col justify-center h-full">
                <div className="flex items-center gap-1.5">
                  <RefreshCw className="h-3 w-3" />
                  <span className="font-bold text-sm">Reconnect</span>
                </div>
                <div className="text-xs opacity-90 mt-0.5">Refresh</div>
              </div>
            </div>
            
            {/* Refresh Button */}
            <div className="bg-gradient-to-br from-orange-400 to-red-500 text-white px-3 py-2 rounded-lg border-2 border-orange-600 shadow-lg w-[110px] h-[60px] cursor-pointer hover:scale-105 transition-transform"
              onClick={handleManualRefresh}
            >
              <div className="flex flex-col justify-center h-full">
                <div className="flex items-center gap-1.5">
                  <RefreshCw className="h-3 w-3" />
                  <span className="font-bold text-sm">Refresh</span>
                </div>
                <div className="text-xs opacity-90 mt-0.5">Data</div>
              </div>
            </div>
            
            {/* Export Button */}
            <div className="bg-gradient-to-br from-indigo-400 to-blue-600 text-white px-3 py-2 rounded-lg border-2 border-indigo-600 shadow-lg w-[110px] h-[60px] cursor-pointer hover:scale-105 transition-transform"
              onClick={exportToExcel}
            >
              <div className="flex flex-col justify-center h-full">
                <div className="flex items-center gap-1.5">
                  <Download className="h-3 w-3" />
                  <span className="font-bold text-sm">Export</span>
                </div>
                <div className="text-xs opacity-90 mt-0.5">Excel</div>
              </div>
            </div>
            <Button onClick={handleExportExcel} variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export Excel
            </Button>
          </div>
        </div>

        {/* Time Range Selector */}
        <div className="flex gap-2">
          {['today', '24hours', 'yesterday', '7days', '30days'].map((range) => (
            <Button
              key={range}
              onClick={() => setTimeRange(range)}
              variant={timeRange === range ? 'default' : 'outline'}
              size="sm"
              className={timeRange === range ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
            >
              {range === '24hours' ? 'Last 24 Hours' :
               range === '7days' ? 'Last 7 Days' :
               range === '30days' ? 'Last 30 Days' :
               range.charAt(0).toUpperCase() + range.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 mb-2">
        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
          <CardContent className="p-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-xs">Live Visitors</p>
                <p className="text-xl font-bold mt-0.5">{liveVisitors.length}</p>
                <p className="text-emerald-100 text-xs mt-0.5">Active now</p>
              </div>
              <Eye className="h-6 w-6 text-emerald-200 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-xs">Total Visits</p>
                <p className="text-xl font-bold mt-0.5">{analytics?.totalVisits || 0}</p>
                <p className="text-blue-100 text-xs mt-0.5">{timeRange}</p>
              </div>
              <Users className="h-6 w-6 text-blue-200 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-xs">Unique Visitors</p>
                <p className="text-xl font-bold mt-0.5">{analytics?.uniqueVisitors || 0}</p>
                <p className="text-purple-100 text-xs mt-0.5">Unique IPs</p>
              </div>
              <Globe className="h-6 w-6 text-purple-200 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
          <CardContent className="p-2">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-xs">Avg Session Time</p>
                <p className="text-xl font-bold mt-0.5">{Math.floor(analytics?.avgSessionTime || 0)}s</p>
                <p className="text-orange-100 text-xs mt-0.5">Per visitor</p>
              </div>
              <Clock className="h-6 w-6 text-orange-200 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Device Breakdown Mini Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
        <Card className="border-l-4 border-blue-500">
          <CardContent className="p-1.5 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Monitor className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-xs text-gray-600">Desktop</p>
                <p className="text-lg font-bold text-gray-900">{desktopCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-purple-500">
          <CardContent className="p-1.5 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Smartphone className="h-5 w-5 text-purple-600" />
              <div>
                <p className="text-xs text-gray-600">Mobile</p>
                <p className="text-lg font-bold text-gray-900">{mobileCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-green-500">
          <CardContent className="p-1.5 flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Tablet className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-xs text-gray-600">Tablet</p>
                <p className="text-lg font-bold text-gray-900">{tabletCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Map, Live Visitors, and Traffic Sources - All in One Line */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 mb-2">
        {/* Visitor Locations Map */}
        <div>
          <LiveVisitorsMap visitors={liveVisitors} />
        </div>
        
        {/* Live Visitors List */}
        <div>
          <VisitorDetails 
            visitors={liveVisitors} 
            onSelectSession={setSelectedSession}
          />
        </div>
        
        {/* Traffic Sources */}
        <div>
          <TrafficSources timeRange={timeRange} />
        </div>
      </div>

      {/* Activity Feed, Notifications, and Analytics - Three Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <ActivityFeed />
        <NotificationBox />
        <AnalyticsCharts analytics={analytics} timeRange={timeRange} />
      </div>
    </div>
  );
};

export default LiveTrafficDashboard;